<template>
  <div>
    <div v-for="item in questions" :key="item.name" class="my-4 border-bottom">
      <div class="d-flex justify-content-between align-items-center">
        <div>{{ item.title }}</div>
        <custom-radio-button
          :change-event="(val) => setAnswer(item, val)"
          :question-lists="item"
        />
      </div>
      <div v-if="!isReadOnly">
        <validation-provider
          v-slot="{ errors }"
          :name="item.title"
          :rules="isSubmitting ? 'required' : ''"
          :vid="`question-${item.name}`"
        >
          <custom-input
            custom-error-msg="Favor seleccionar Sí o No"
            :error="errors[0]"
            type="hidden"
            :value="[false, true].includes(model[item.name]) ? '1' : null"
          />
        </validation-provider>
      </div>
      <div class="mx-3">
        <div v-if="isSelected(item, 'rubricRequired')">
          <div class="mb-4">
            <div class="row">
              <div class="col-12 col-md-6">
                <validation-provider
                  v-slot="{ errors }"
                  :custom-messages="{
                    min_value: 'La puntuación mínima debe ser mayor que 0.',
                    max_value:
                      'La puntuación mínima debe ser menor o igual que 100.',
                  }"
                  rules="min_value:1|max_value:100"
                >
                  <custom-input
                    v-model.lazy.number="model.minimumScore"
                    :disabled="isReadOnly"
                    :error="errors[0]"
                    label="Puntuación Mínima:"
                    placeholder="0%"
                    type="number"
                  />
                </validation-provider>
              </div>
            </div>
            <div class="row my-4">
              <div class="col-12 col-md-6">
                <div class="d-flex flex-column">
                  <custom-drop-down
                    v-model="rubric"
                    :disabled="isReadOnly"
                    fieldkey="id"
                    fieldname="name"
                    label="Rúbrica:"
                    :options="rubrics"
                    placeholder="Seleccione"
                  />
                  <action-button
                    class="mt-3 align-end"
                    i="fas fa-file-plus"
                    variant="success"
                    @click="addRubric()"
                  >
                    Añadir
                  </action-button>
                </div>
              </div>
              <div class="col-12 col-md border-left">
                <label class="data-name">Rúbrica(s) Seleccionado(s):</label>
                <custom-chips-group
                  class="custom-chips-group"
                  :disabled="isReadOnly"
                  no-background
                  no-wrap
                  object-text="name"
                  :on-item-deleted="removeRubric"
                  :selected-items="model.rubrics"
                  title=""
                />
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="isSelected(item, 'servicesRenewableRequired')">
          <div class="mb-4">
            <div class="row">
              <div class="col-12">
                <label class="data-name">
                  ¿Por cuánto tiempo?
                </label>
              </div>
              <div class="col-12 col-md-3">
                <validation-provider
                  v-slot="{ errors }"
                  rules="required|min_value:1"
                >
                  <custom-input
                    v-model.lazy.number="model.servicesRenewableDuration"
                    custom-error-msg="El campo es requerido y debe ser mayor a 0"
                    :disabled="isReadOnly"
                    :error="errors[0]"
                    :min="0"
                    type="number"
                  />
                </validation-provider>
              </div>
              <div class="col-12 col-md-3">
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-drop-down
                    v-model="servicesRenewableDurationType"
                    :clearable="false"
                    custom-error-msg="El campo es requerido"
                    :disabled="isReadOnly"
                    :error="errors[0]"
                    fieldkey="id"
                    fieldname="name"
                    :options="durationTypes"
                    placeholder="Seleccione"
                  />
                </validation-provider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
import CustomRadioButton from '@/components/common/CustomRadioButton';
import ActionButton from 'ActionButton';
import CustomChipsGroup from 'CustomChipsGroup';

import { getAllBy as getAllRubrics } from '@/modules/rubric/api.js';

import RubricStatus from '@/utils/shared/constants/rubricStatus.js';
import ServiceTypes from '@/utils/constants/proposalServiceTypes';
import DurationTypes from '@/utils/constants/contractRenewalDurationTypes';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';

export default {
  name: 'Configuration',
  components: {
    ValidationProvider,
    CustomInput,
    CustomDropDown,
    CustomRadioButton,
    ActionButton,
    CustomChipsGroup,
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: Object,
      required: true,
    },
    services: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
    isSubmitting: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      rubric: null,
      rubrics: [],
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    questions() {
      return [
        this.newQuestion('rubricRequired', '¿El pliego contiene rúbrica?'),
        this.newQuestion(
          'proposalQuestionnaireRequired',
          '¿El pliego contiene cuestionario de propuesta?'
        ),
        this.newQuestion(
          'ssnRequired',
          '¿En este pliego será requerido el seguro social patronal del proponente?'
        ),
        this.newQuestion(
          'evaluationCommitteeRequired',
          '¿Este pliego va a requerir un comité evaluador?'
        ),
        this.newQuestion(
          'servicesRenewableRequired',
          '¿Los servicios podrán ser renovados?'
        ),
        this.newQuestion(
          'blindEvaluationRequired',
          '¿El Proceso de evaluación será sin conocer el nombre del proponente?'
        ),
        this.newQuestion(
          'screeningProcessRequired',
          '¿Este proceso de competencia tendrá proceso de cernimiento?'
        ),
      ];
    },
    isSelected() {
      return ({ name }, prop) => name === prop && this.model[prop];
    },
    servicesRenewableDurationType: {
      get() {
        return (
          this.durationTypes.find(
            (x) => x.id === this.model.servicesRenewableDurationType
          ) ?? this.durationTypes[0]
        );
      },
      set(option) {
        this.model.servicesRenewableDurationType = option?.id;
      },
    },
    durationTypes() {
      return DurationTypes;
    },
  },
  watch: {
    'model.service': {
      immediate: true,
      async handler(val) {
        if (!val) this.model.rubrics = [];
        else await this.getRubrics();
        this.rubrics = this.rubrics.filter(
          (x) => !this.model.rubrics.map((y) => y.id).includes(x.id)
        );
      },
    },
  },
  methods: {
    async setAnswer({ name }, { id }) {
      this.model[name] = id;
      if (name === 'rubricRequired') {
        if (id) {
          await this.getRubrics();

          return;
        }

        this.model.rubrics = [];
        this.model.minimumScore = null;
      }
    },
    newQuestion(name, title) {
      const options = [
        {
          id: true,
          label: 'Si',
          check: this.model[name] === true,
          disabled: this.isReadOnly,
        },
        {
          id: false,
          label: 'No',
          check: this.model[name] === false,
          disabled: this.isReadOnly,
        },
      ];
      return { name, title, options };
    },
    addRubric() {
      if (!this.rubric) return;

      const exist = this.model.rubrics.some(
        (y) =>
          y.evaluationTypeId === this.rubric.evaluationTypeId &&
          y.serviceId === this.rubric.serviceId
      );

      if (exist) {
        this.ShowToast(
          'Error',
          'Este pliego cuenta con una rúbrica con el mismo tipo de evaluación.',
          'error'
        );

        return;
      }

      this.model.rubrics.push(this.rubric);
      const index = this.rubrics.findIndex((y) => y.id === this.rubric.id);
      this.rubrics.splice(index, 1);
      this.rubric = null;
    },
    removeRubric(item) {
      const index = this.model.rubrics.findIndex((y) => y.id === item.id);
      this.model.rubrics.splice(index, 1);
      this.rubrics.push(item);
      this.rubrics.sort((a, b) => a.id - b.id);
    },
    async getRubrics() {
      this.rubric = null;
      this.rubrics = [];
      if (!this.model.area || !this.model.service) return;

      const { data } = await getAllRubrics({
        areaId: this.model.area.id,
        serviceId: this.model.service.id,
        statusIds: [RubricStatus.Published],
      });
      this.rubrics = data;

      if (this.model.rubricRequired && !this.rubrics.length) {
        const ids = this.model.rubrics?.map((x) => x.serviceId) ?? [];
        const hasEducational = ids.includes(ServiceTypes.Educational);
        const hasTherapyRelated = ids.includes(
          ServiceTypes.EvaluationAndOrTherapyRelatedServices
        );
        if (hasEducational && hasTherapyRelated) {
          this.model.service = this.services.find(
            (x) => x.id === ServiceTypes.EducationalAndRelatedServices
          );
        }
      }
    },
  },
};
</script>
<style scoped>
.align-end {
  align-self: flex-end;
}

.custom-chips-group ::v-deep .chips-container {
  margin: 0 !important;
  padding: 0 !important;
}
.custom-chips-group ::v-deep > div {
  margin: 0 !important;
}

.custom-chips-group ::v-deep .mb-2 {
  margin-bottom: 0 !important;
}

.custom-chips-group ::v-deep .chips {
  margin-top: 0 !important;
  margin-bottom: 10px !important;
}
</style>
